import { string } from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { I18n } from 'react-redux-i18n';
import { push } from 'redux-first-history';
import apiInstance2 from '../../../services/apiService';
import PageWrapper from '../../PageWrapper';
import ButtonGroup from '../../reusable/Buttons/ButtonGroup';
import Spinner from '../../reusable/Spinner';
import SimplifiedTable from '../../reusable/Tables/SimplifiedTable';
import TableTabs from '../../reusable/TableTabs';
import BioDataView from './BioDataView';
import HistogramCell from './HistogramCell';
import HistogramView from './HistogramView';
import NormsHeader from './NormsHeader';
import NotesView from './NotesView';
import SteTable from './SteTable';

const NormScales = ({ normId }) => {
  const dispatch = useDispatch();
  const onBackButtonClick = () => dispatch(push('/settings/norms'));
  const [norms, setNorms] = useState([]);
  const [normData, setNormData] = useState({});
  const [bioData, setBioData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [normDescription, setNormDescription] = useState('');

  const [shouldRenderBioData, setShouldRenderBioData] = useState(false);

  let applyRemappedAge = false;
  let remappedAge = '';

  let applyRemappedGender = false;
  let remappedGender = '';

  let applyRemappedEthnicity = false;
  let remappedEthnicity = '';

  let applyRemappedEducation = false;
  let remappedEducation = '';

  let applyRemappedOccupation = false;
  let remappedOccupation = '';

  let applyRemappedIndustry = false;
  let remappedIndustry = '';

  let applyRemappedFirstLanguage = false;
  let remappedFirstLanguage = '';

  let applyRemappedSector = false;
  let remappedSector = '';

  const categoriesValuesValidations = (age, sex, ethnicity, education, occupation, industry, firstLanguage, sector) => {
    return (
      age === '' &&
      sex === '' &&
      ethnicity === '' &&
      education === '' &&
      occupation === '' &&
      industry === '' &&
      firstLanguage === '' &&
      sector === ''
    );
  };

  /**
   * Validation of AGE CATEGORY with single format, defined on BOOK OF NORMS.
   * Single string with below format information: 'Aged < 20: 57 (0.6%); Aged 20 -29: 2153 (23.9%); Aged 30 - 39: 2729 (30.3%); Aged 40 - 49: 2506 (27.8%); Aged 50 - 59: 1380 (15.3%); Undeclared: 1 (0%); Aged >= 60: 181 (2%)'
   */
  const ageCategoryValidations = (age) => {
    if (age && age.split(';')[0].startsWith('Aged <')) {
      applyRemappedAge = true;
      const extractCountRegex = /:\s?(\d+(\.\d+)?)\s?\(/;
      const extractPercentageRegex = /\((\d+(\.\d+)?)%\)/;
      const ageValues = [];

      if (age.split(';').length !== 7) return;

      age.split(';').forEach((value) => {
        const countResult = value.match(extractCountRegex);
        const percentageResult = value.match(extractPercentageRegex);
        if (countResult && percentageResult) {
          return ageValues.push(`${countResult[1]}:${percentageResult[1]}`);
        }
      });

      const orderedAgeGroups = [
        ageValues[0],
        ageValues[1],
        ageValues[2],
        ageValues[3],
        ageValues[4],
        ageValues[6],
        ageValues[5],
      ];

      remappedAge = orderedAgeGroups;
    }

    const mappedAge = {
      name: 'age',
      values: applyRemappedAge ? remappedAge : '',
      graghType: 'barChart',
      FOLLOW_BOOK_OF_NORMS_FORMAT: true,
    };

    return [mappedAge];
  };

  /**
   * Validation of SEX CATEGORY with multiple formats, defined below:
   * 1. Single string with following format information: '12;12;12'
   * 2. Single string with following format information: 'M:12;F:12;U:12'
   * 3. Single string with following format information: 'M=12;F=12;U=12'
   * 4. Single string with following format information: 'Males: 5328 (59.2%); Females: 3178 (35.3%); Undeclared: 501 (5.5%)'
   */
  const genderCategoryValidations = (sex) => {
    let mappedGender = '';
    let FOLLOW_BOOK_OF_NORMS_FORMAT = false;
    if (sex) {
      // Format validations 1.
      if (sex.split(';')[0].startsWith('M:') || sex.split(';')[0].startsWith('M=')) {
        applyRemappedGender = true;
        const genderValues = [];
        sex.split(';').forEach((value) => {
          return value.includes(':') ? genderValues.push(value.split(':')[1]) : genderValues.push(value.split('=')[1]);
        });

        const maleGender = `M:${genderValues[0]}`;
        const femaleGender = `F:${genderValues[1]}`;
        const unspecifiedGender = `U:${genderValues[2]}`;

        remappedGender = `${maleGender};${femaleGender};${unspecifiedGender}`;
        FOLLOW_BOOK_OF_NORMS_FORMAT = false;
      }
      // Format validations 2. and 3.
      if (!sex.split(';')[0].startsWith('M:') && !sex.split(';')[0].startsWith('M=')) {
        applyRemappedGender = true;
        const genderValues = [];
        sex.split(';').forEach((value) => {
          return genderValues.push(value);
        });

        const maleGender = `M:${genderValues[0]}`;
        const femaleGender = `F:${genderValues[1]}`;
        const unspecifiedGender = `U:${genderValues[2]}`;

        remappedGender = `${maleGender};${femaleGender};${unspecifiedGender}`;
        FOLLOW_BOOK_OF_NORMS_FORMAT = false;
      }
      // Format validations 4.
      if (sex.split(';')[0].startsWith('Males')) {
        applyRemappedGender = true;
        const extractGroupNameRegex = /^([^:]+):/;
        const extractAgeRegex = /:\s?(\d+(\.\d+)?)\s?\(/;
        const extractPercentageRegex = /\((\d+(\.\d+)?)%\)/;
        const genderValues = [];

        if (sex.split(';').length !== 3) return;

        sex.split(';').forEach((value) => {
          const groupNameResult = value.match(extractGroupNameRegex);
          const countResult = value.match(extractAgeRegex);
          const percentageResult = value.match(extractPercentageRegex);
          if (countResult && percentageResult) {
            return genderValues.push(`${groupNameResult[1]}:${countResult[1]}:${percentageResult[1]}`);
          }
        });

        remappedGender = genderValues;
        FOLLOW_BOOK_OF_NORMS_FORMAT = true;
      }
      mappedGender = {
        name: 'gender',
        values: applyRemappedGender ? remappedGender : sex,
        graghType: 'pieChart',
        FOLLOW_BOOK_OF_NORMS_FORMAT,
      };
    }
    return [mappedGender];
  };

  /**
   * Validation of categories with single format, defined into respondentSelectOptions.js (file)
   * ethnicity => initial code 'e-'
   * education => initial code 'q-'
   * occupation => initial code 'o-'
   * industry => initial code 'i-'
   * firstLanguage => initial code 'l-'
   * sector => initial code 's-'
   */
  const categoryCodeValidations = (ethnicity, education, occupation, industry, firstLanguage, sector) => {
    /** ethnicity */
    if (ethnicity && !ethnicity.split(';')[0].startsWith('e-')) {
      applyRemappedEthnicity = true;
      remappedEthnicity = '';
    }
    const mappedethnicity = {
      name: 'ethnicity',
      values: applyRemappedEthnicity ? remappedEthnicity : ethnicity,
      graghType: 'pieChart',
      FOLLOW_BOOK_OF_NORMS_FORMAT: false,
    };
    /** education */
    if (education && !education.split(';')[0].startsWith('q-')) {
      applyRemappedEducation = true;
      remappedEducation = '';
    }
    const mappedEducation = {
      name: 'education',
      values: applyRemappedEducation ? remappedEducation : education,
      graghType: 'pieChart',
      FOLLOW_BOOK_OF_NORMS_FORMAT: false,
    };
    /** occupation */
    if (occupation && !occupation.split(';')[0].startsWith('o-')) {
      applyRemappedOccupation = true;
      remappedOccupation = '';
    }
    const mappedOccupation = {
      name: 'occupation',
      values: applyRemappedOccupation ? remappedOccupation : occupation,
      graghType: 'pieChart',
      FOLLOW_BOOK_OF_NORMS_FORMAT: false,
    };
    /** industry */
    if (industry && !industry.split(';')[0].startsWith('i-')) {
      applyRemappedIndustry = true;
      remappedIndustry = '';
    }
    const mappedIndustry = {
      name: 'industry',
      values: applyRemappedIndustry ? remappedIndustry : industry,
      graghType: 'pieChart',
      FOLLOW_BOOK_OF_NORMS_FORMAT: false,
    };
    /** firstLanguage */
    if (firstLanguage && !firstLanguage.split(';')[0].startsWith('l-')) {
      applyRemappedFirstLanguage = true;
      remappedFirstLanguage = '';
    }
    const mappedFirstLanguage = {
      name: 'first language',
      values: applyRemappedFirstLanguage ? remappedFirstLanguage : firstLanguage,
      graghType: 'pieChart',
      FOLLOW_BOOK_OF_NORMS_FORMAT: false,
    };
    /** sector */
    if (sector && !sector.split(';')[0].startsWith('s-')) {
      applyRemappedSector = true;
      remappedSector = '';
    }
    const mappedSector = {
      name: 'sector',
      values: applyRemappedSector ? remappedSector : sector,
      graghType: 'pieChart',
      FOLLOW_BOOK_OF_NORMS_FORMAT: false,
    };

    return [mappedethnicity, mappedFirstLanguage, mappedEducation, mappedIndustry, mappedOccupation, mappedSector];
  };

  useEffect(() => {
    apiInstance2
      .get(`/api/v2/norms/${normId}`)
      .then((response) => {
        const {
          description = '',
          type,
          creationDate,
          normID,
          name,
          sampleSize,
          testID,
          scales,
          age = '',
          sex = '',
          ethnicity = '',
          education = '',
          occupation = '',
          industry = '',
          firstLanguage = '',
          sector = '',
        } = response.data;

        setNormData({ description, type, creationDate, name, sampleSize, testID, normID });
        setNorms(scales.map((details) => ({ ...details, sampleSize })));
        setNormDescription(description);

        const isAllCategoriesEmpty = categoriesValuesValidations(
          age,
          sex,
          ethnicity,
          education,
          occupation,
          industry,
          firstLanguage,
          sector,
        );
        if (!isAllCategoriesEmpty) {
          const ageInfo = ageCategoryValidations(age);
          const genderInfo = genderCategoryValidations(sex);
          const bioInfo = categoryCodeValidations(ethnicity, education, occupation, industry, firstLanguage, sector);
          setBioData([...ageInfo, ...genderInfo, ...bioInfo]);
          setShouldRenderBioData(!isAllCategoriesEmpty);
        }

        setIsLoading(false);
      })
      .catch(() => {
        setIsLoading(false);
      });

    return () => {};
  }, [normId]);

  const columns = [
    { Header: 'Scale', name: 'scaleName', accessor: 'scaleName' },
    { Header: 'Mean', name: 'mean', accessor: 'mean' },
    { Header: 'SD', name: 'sd', accessor: 'sd' },
    { Header: 'Sample Size', name: 'sampleSize', accessor: 'sampleSize' },
    { Header: 'Histogram', name: 'histogram', accessor: 'histogram', Cell: HistogramCell },
  ];

  const hasHistogram = norms.find((scale) => scale.frequency);

  const tabs = [
    { name: 'Main Details', label: 'Main Details' },
    ...(normData.type === 'F' ? [{ name: 'Norm Table', label: 'Norm Table' }] : []),
    ...(normData.type === 'F' && hasHistogram ? [{ name: 'Frequency Histograms', label: 'Frequency Histograms' }] : []),
    ...(shouldRenderBioData ? [{ name: 'Bio Data', label: 'Bio Data' }] : []),
    ...(normDescription !== '' ? [{ name: 'Notes', label: 'Notes' }] : []),
  ];

  const [activeTab, setActiveTab] = useState(tabs[0]);

  const onTabClick = (tab) => {
    setActiveTab(tab);
  };

  const groups = [
    {
      name: 'sten',
      title: I18n.t('Sten'),
    },
    {
      name: 'stanine',
      title: I18n.t('Stanine'),
    },
  ];

  const [activeGroup, setActiveGroup] = useState(groups[0]);
  const title = normData.name ? normData.name : I18n.t('Norms');

  return (
    <PageWrapper title={title} backButtonHandler={onBackButtonClick} buttons={[]}>
      <NormsHeader data={normData} />
      <TableTabs
        tabs={tabs}
        activeTab={activeTab}
        clickHandler={onTabClick}
        withChildren={activeTab.name === 'Norm Table'}
      >
        {activeTab.name === 'Norm Table' && (
          <ButtonGroup groups={groups} activeGroup={activeGroup} setActiveGroup={setActiveGroup} />
        )}
      </TableTabs>
      {activeTab.name === 'Main Details' && (
        <SimplifiedTable
          columns={columns}
          data={norms}
          onSelectChange={() => {}}
          idAccessor="normID"
          entityName="norm"
          disableSelect
          bottomOffset={170}
        />
      )}
      {activeTab.name === 'Norm Table' && <SteTable norms={norms} activeGroup={activeGroup} />}
      {activeTab.name === 'Frequency Histograms' && <HistogramView norms={norms} />}
      {shouldRenderBioData && activeTab.name === 'Bio Data' && <BioDataView bioData={bioData} />}
      {activeTab.name === 'Notes' && <NotesView description={normDescription} />}
      <Spinner isLoading={isLoading} full />
    </PageWrapper>
  );
};

NormScales.propTypes = {
  // normId: string.isRequired,
};

export default NormScales;
